import { useMemo } from 'react';
import { INCIDENT } from 'components/IAP/ICSConstants';
import { useIncidentActions } from 'components/IAP/hooks/incidentActionHooks';
import type { ActionEntity } from 'components/IAP/hooks/useScopedActionForm';
import type { IncidentPosition } from 'hooks/incident/useRoster';
import {
  positionTaskStatusCharts,
  positionTaskStatusColor,
  type PositionTaskStatusChart,
  type PositionTaskStatusSlice,
} from './positionTaskStatusSlices';

const PIE_RADIUS = 42;
const PIE_CENTER = 50;

function polarToCartesian(angleDeg: number): { x: number; y: number } {
  const radians = ((angleDeg - 90) * Math.PI) / 180;
  return {
    x: PIE_CENTER + PIE_RADIUS * Math.cos(radians),
    y: PIE_CENTER + PIE_RADIUS * Math.sin(radians),
  };
}

function slicePath(startDeg: number, sweepDeg: number): string {
  if (sweepDeg >= 360 - 0.01) {
    return [
      `M ${PIE_CENTER} ${PIE_CENTER}`,
      `m 0 ${-PIE_RADIUS}`,
      `a ${PIE_RADIUS} ${PIE_RADIUS} 0 1 1 0 ${PIE_RADIUS * 2}`,
      `a ${PIE_RADIUS} ${PIE_RADIUS} 0 1 1 0 ${-PIE_RADIUS * 2}`,
      'Z',
    ].join(' ');
  }
  const start = polarToCartesian(startDeg);
  const end = polarToCartesian(startDeg + sweepDeg);
  const largeArc = sweepDeg > 180 ? 1 : 0;
  return [
    `M ${PIE_CENTER} ${PIE_CENTER}`,
    `L ${start.x} ${start.y}`,
    `A ${PIE_RADIUS} ${PIE_RADIUS} 0 ${largeArc} 1 ${end.x} ${end.y}`,
    'Z',
  ].join(' ');
}

function pieArcs(slices: PositionTaskStatusSlice[], total: number) {
  let cursor = 0;
  return slices.map((slice) => {
    const sweep = (slice.count / total) * 360;
    const start = cursor;
    cursor += sweep;
    return { ...slice, start, sweep };
  });
}

function PositionStatusPie({ chart }: { chart: PositionTaskStatusChart }) {
  const arcs = chart.total > 0 ? pieArcs(chart.slices, chart.total) : [];

  return (
    <article
      className="tw:flex tw:flex-col tw:gap-3 tw:rounded-md tw:border tw:p-3"
      aria-label={`Task status for ${chart.positionName}`}
    >
      <h3 className="tw:m-0 tw:text-sm tw:font-semibold">
        {chart.positionName}
      </h3>
      <div className="tw:flex tw:items-center tw:gap-3">
        <svg
          viewBox="0 0 100 100"
          className="tw:size-24 tw:shrink-0"
          role="img"
          aria-label={
            chart.total === 0
              ? `${chart.positionName} has no tasks`
              : `${chart.positionName} has ${chart.total} tasks`
          }
        >
          {chart.total === 0 ? (
            <circle
              cx={PIE_CENTER}
              cy={PIE_CENTER}
              r={PIE_RADIUS}
              fill="none"
              stroke="currentColor"
              strokeWidth="8"
              className="tw:text-muted-foreground/40"
            />
          ) : (
            arcs.map((arc) => (
              <path
                key={arc.status}
                d={slicePath(arc.start, arc.sweep)}
                fill={positionTaskStatusColor(arc.status)}
              />
            ))
          )}
        </svg>
        {chart.total === 0 ? (
          <p className="tw:m-0 tw:text-xs tw:text-muted-foreground">No tasks</p>
        ) : (
          <ul className="tw:m-0 tw:list-none tw:space-y-1 tw:p-0 tw:text-xs">
            {chart.slices.map((slice) => (
              <li
                key={slice.status}
                className="tw:flex tw:items-center tw:gap-2"
              >
                <span
                  className="tw:size-2 tw:shrink-0 tw:rounded-full"
                  style={{
                    backgroundColor: positionTaskStatusColor(slice.status),
                  }}
                />
                <span>
                  {slice.status} ({slice.count})
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </article>
  );
}

export function RosterAnalyticsView({
  positions,
}: {
  positions: IncidentPosition[];
}) {
  const { data: actions = [] } = useIncidentActions(INCIDENT);
  const charts = useMemo(
    () =>
      positionTaskStatusCharts(
        actions as ActionEntity[],
        positions.map((position) => ({
          id: position.id,
          name: position.name,
        }))
      ),
    [actions, positions]
  );

  if (positions.length === 0) {
    return (
      <p className="tw:m-0 tw:text-sm tw:text-muted-foreground">
        No roster positions to chart.
      </p>
    );
  }

  return (
    <div className="tw:grid tw:gap-3 tw:sm:grid-cols-2 tw:xl:grid-cols-3">
      {charts.map((chart) => (
        <PositionStatusPie key={chart.positionId} chart={chart} />
      ))}
    </div>
  );
}
